/**
 * Voice Message Recorder Component
 * Records and uploads voice messages for CRM chat.
 * Features:
 * - Record audio using Web Audio API
 * - Visual recording indicator
 * - Duration limit enforcement
 * - Playback preview before sending
 */

import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from './ui/button';
import { 
  Mic, 
  MicOff, 
  Square, 
  Play, 
  Pause, 
  Send, 
  Trash2,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const MAX_DURATION = 120; // 2 minutes max

const VoiceMessageRecorder = ({
  onSend,
  contactId = null,
  chatType = 'internal',
  // Optional: when chatType is 'hod_comments', this records WHICH HOD bucket
  // the CEO targeted (all / hod_sales / hod_marketing). Mirrored from the
  // text comment's target_hod so the receiving HOD can see it.
  targetHod = null,
  compact = false,
  // Block recording entirely (e.g. no recipient picked yet). Prevents the
  // silent "voice saved with contact_id=null and never reaches anyone" bug.
  disabled = false,
  disabledReason = '',
}) => {
  const { token } = useAuth();
  const { isDarkMode } = useTheme();
  
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioRef = useRef(new Audio());
  const streamRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, [audioUrl]);

  // Format duration as mm:ss
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: mediaRecorder.mimeType });
        setAudioBlob(blob);
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
        audioRef.current.src = url;
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorder.start(100);
      setIsRecording(true);
      setDuration(0);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setDuration(prev => {
          if (prev >= MAX_DURATION - 1) {
            stopRecording();
            return prev;
          }
          return prev + 1;
        });
      }, 1000);
      
    } catch (error) {
      console.error('Error starting recording:', error);
      toast.error('Could not access microphone. Please check permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const togglePlayback = () => {
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
      
      audioRef.current.onended = () => {
        setIsPlaying(false);
      };
    }
  };

  const discardRecording = () => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioBlob(null);
    setAudioUrl(null);
    setDuration(0);
    setIsPlaying(false);
    audioRef.current.pause();
    audioRef.current.src = '';
  };

  const uploadVoiceMessage = async () => {
    if (!audioBlob || !token) return;
    
    setIsUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'voice_message.webm');
      formData.append('duration_seconds', duration.toString());
      formData.append('chat_type', chatType);
      if (contactId) {
        formData.append('contact_id', contactId);
      }
      if (targetHod) {
        formData.append('target_hod', targetHod);
      }
      
      const response = await fetch(`${API_BASE}/api/voice-messages/upload?token=${token}`, {
        method: 'POST',
        body: formData
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Voice message sent!');
        if (onSend) {
          onSend(data.message);
        }
        discardRecording();
      } else {
        toast.error(data.error || 'Failed to send voice message');
      }
    } catch (error) {
      console.error('Error uploading voice message:', error);
      toast.error('Failed to upload voice message');
    } finally {
      setIsUploading(false);
    }
  };

  // Compact mode - just a mic button
  if (compact && !isRecording && !audioBlob) {
    return (
      <Button
        variant="ghost"
        size="icon"
        onClick={() => {
          if (disabled) {
            toast.error(disabledReason || 'Pick a recipient first');
            return;
          }
          startRecording();
        }}
        disabled={disabled}
        className="h-10 w-10"
        title={disabled ? (disabledReason || 'Recipient required') : 'Record voice message'}
        data-testid="voice-record-button"
      >
        <Mic className="h-5 w-5" />
      </Button>
    );
  }

  return (
    <div className={`flex items-center gap-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
      {/* Recording state */}
      {isRecording && (
        <>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
            <span className="text-sm font-mono">{formatDuration(duration)}</span>
          </div>
          <Button
            variant="destructive"
            size="icon"
            onClick={stopRecording}
            className="h-10 w-10"
            title="Stop recording"
          >
            <Square className="h-5 w-5" />
          </Button>
        </>
      )}
      
      {/* Preview state */}
      {!isRecording && audioBlob && (
        <>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={togglePlayback}
              className="h-10 w-10"
              title={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
            </Button>
            <span className="text-sm font-mono">{formatDuration(duration)}</span>
          </div>
          
          <Button
            variant="ghost"
            size="icon"
            onClick={discardRecording}
            className="h-10 w-10 text-red-500 hover:text-red-600"
            title="Discard"
          >
            <Trash2 className="h-5 w-5" />
          </Button>
          
          <Button
            variant="default"
            size="icon"
            onClick={uploadVoiceMessage}
            disabled={isUploading}
            className={`h-10 w-10 ${isDarkMode ? 'bg-purple-600 hover:bg-purple-500' : 'bg-purple-500 hover:bg-purple-600'}`}
            title="Send"
          >
            {isUploading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Send className="h-5 w-5" />
            )}
          </Button>
        </>
      )}
      
      {/* Idle state */}
      {!isRecording && !audioBlob && (
        <Button
          variant="ghost"
          size="icon"
          onClick={startRecording}
          className="h-10 w-10"
          title="Record voice message"
          data-testid="voice-record-button"
        >
          <Mic className="h-5 w-5" />
        </Button>
      )}
    </div>
  );
};

export default VoiceMessageRecorder;
